/**
 * LearnBoard — the learn agent's own face: the skills the learner is working
 * toward and what to work on next because of them.
 *
 * Board-first, same pattern as the mentor board / team access / the Care Planner
 * schedule: opening the agent shows the learning experience, not an empty
 * chat that makes the nurse guess what to type. The conversation is the
 * side-channel the connected wrapper mounts underneath — it's how you ASK for
 * something ("mach mir ein Übungsquiz"), not the only thing the agent has to
 * show.
 *
 * Presentational only: it renders what it's given. Fetching and folding belong
 * to LearnBoardConnected.
 *
 * Order = the learner's task: their targets (what they're committed to), then
 * the modules those targets name (what to pick up for them).
 *
 * Whether a target is REACHED is never computed here. It arrives as `state` on
 * the wire, derived by the fold that owns the records. The rows themselves are
 * the shared ones (components/training/TargetRow), the same rows the mentor
 * board renders, and `today` is handed to them so an open target's lateness
 * stays a comparison of two civil days the caller supplies.
 */

import { useTranslation } from 'react-i18next'
import { Lightbulb, Target } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/clinical'
import { TargetRow } from '@/components/training'
import type { SkillTarget } from '@frontend/hooks/useSkills'

import type { Recommendation } from './types'

export interface LearnBoardProps {
  /** Every target on the learner's fold, already ordered (sortTargets). */
  targets: SkillTarget[]
  /**
   * Today in the reader's calendar (`todayCivilDate`), the day an open target's
   * `due_date` is late against. Passed in rather than read here so the rule
   * stays a pure comparison of two civil days.
   */
  today: string
  /** The published modules the open targets name, in target order. */
  recommended: Recommendation[]

  targetsLoading?: boolean
  targetsError?: boolean
  onRetryTargets?: () => void
  /** The recommendation list reads the targets AND the catalogue, so it is
   *  loading/failed if either is. */
  recommendedLoading?: boolean
  recommendedError?: boolean
  onRetryRecommended?: () => void

  className?: string
}

export function LearnBoard({
  targets,
  today,
  recommended,
  targetsLoading,
  targetsError,
  onRetryTargets,
  recommendedLoading,
  recommendedError,
  onRetryRecommended,
  className,
}: LearnBoardProps) {
  const { t } = useTranslation()

  return (
    <div className={cn('mx-auto w-full max-w-3xl space-y-6 p-4', className)}>
      <Section title={t('learn.targets.title')} icon={<Target className="h-4 w-4" />}>
        {targetsLoading ? (
          <Skeleton className="h-16 w-full rounded-[14px]" />
        ) : targetsError ? (
          <LoadRetry message={t('learn.targets.loadError')} onRetry={onRetryTargets} />
        ) : targets.length === 0 ? (
          <EmptyState
            title={t('learn.targets.empty.title')}
            description={t('learn.targets.empty.description')}
          />
        ) : (
          <ul className="space-y-2">
            {targets.map(({ target, skillTitle }) => (
              <TargetRow key={target.id} target={target} skillTitle={skillTitle} today={today} />
            ))}
          </ul>
        )}
      </Section>

      <Section title={t('learn.recommended.title')} icon={<Lightbulb className="h-4 w-4" />}>
        {recommendedLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full rounded-[14px]" />
            <Skeleton className="h-16 w-full rounded-[14px]" />
          </div>
        ) : recommendedError ? (
          <LoadRetry message={t('learn.recommended.loadError')} onRetry={onRetryRecommended} />
        ) : recommended.length === 0 ? (
          <EmptyState
            title={t('learn.recommended.empty.title')}
            description={t('learn.recommended.empty.description')}
          />
        ) : (
          <ul className="space-y-2">
            {/* Plain rows, not buttons: the runner died with the training run
                (a run is a conversation, and the conversation domain is
                tricorder-session's), so there is nothing a tap could start. */}
            {recommended.map(({ component, because }) => (
              <li
                key={component.id}
                className="rounded-[14px] border border-neutral-200 bg-white p-3"
              >
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5">
                    <span className="block truncate text-[15px] font-semibold text-neutral-900">
                      {component.title}
                    </span>
                  </span>
                  <span className="mt-0.5 flex flex-col gap-0.5 text-[12px] text-neutral-500">
                    {/* WHY this is here — the learner's own open target, never
                        a generic nudge. */}
                    <span className="font-semibold text-primary-700">
                      {t('learn.recommended.becauseTarget', { title: because })}
                    </span>
                    {component.description && (
                      <span className="truncate">{component.description}</span>
                    )}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  )
}

function Section({
  title,
  icon,
  children,
}: {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="space-y-3">
      <h2 className="flex items-center gap-2 text-[15px] font-semibold text-neutral-900">
        <span className="text-neutral-400" aria-hidden="true">
          {icon}
        </span>
        {title}
      </h2>
      {children}
    </section>
  )
}

function LoadRetry({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const { t } = useTranslation()
  return (
    <p className="text-[13px] text-neutral-700" role="alert">
      {message}{' '}
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="font-semibold text-primary underline-offset-2 hover:underline"
        >
          {t('actions.retry', 'Retry')}
        </button>
      )}
    </p>
  )
}
